import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { VERSES } from "@/lib/scripture";
import { toast } from "sonner";
import { ArrowLeft, Cross, Flame, CheckCircle2, BookHeart, Plus, Trash2, Layers, ChevronRight, Eye } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ReadAloudButton } from "@/components/ReadAloudButton";

export default function ScripturePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isParent = user?.role === "Parent";
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState("");
  const [verses, setVerses] = useState([]);
  const [stats, setStats] = useState(null);
  const [ref, setRef] = useState("");
  const [text, setText] = useState("");

  const targetId = isParent ? sid : user?.id;

  const load = useCallback(() => {
    if (!targetId) return;
    api.get(`/verses?student_id=${targetId}`).then((r) => setVerses(r.data));
    api.get(`/verses-stats/${targetId}`).then((r) => setStats(r.data));
  }, [targetId]);

  useEffect(() => {
    if (isParent) api.get("/students").then((r) => { setStudents(r.data); if (r.data[0]) setSid(r.data[0].id); });
  }, [isParent]);
  useEffect(() => { load(); }, [load]);

  const preset = (v) => { setRef(v.ref); setText(v.text); };

  const add = async () => {
    if (!ref.trim() || !text.trim()) return toast.error("Add a reference and the verse text.");
    if (isParent && !sid) return toast.error("Choose a student.");
    try {
      await api.post("/verses", { student_id: targetId, reference: ref, text });
      setRef(""); setText(""); toast.success("Verse added!"); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const memorize = async (id) => {
    await api.put(`/verses/${id}/memorized`); toast.success("Well done! 🌱"); load();
  };
  const remove = async (id) => { await api.delete(`/verses/${id}`); load(); };

  const [flash, setFlash] = useState(false);
  const accent = isParent ? "#E07A5F" : "#81B29A";

  return (
    <div>
      <TopBar subtitle="Scripture Memory" />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <button onClick={() => navigate(isParent ? "/parent" : "/student")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B]"><ArrowLeft size={16} /> Back</button>
          {isParent && (
            <select data-testid="scripture-student" value={sid} onChange={(e) => setSid(e.target.value)}
              className="rounded-full border border-[#EAE6DF] bg-white px-5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E07A5F]">
              {students.map((s) => <option key={s.id} value={s.id}>{s.first_name}</option>)}
            </select>
          )}
        </div>

        <div className="flex items-center gap-3 mb-6">
          <span className="grid place-items-center w-11 h-11 rounded-2xl text-white" style={{ background: accent }}><BookHeart size={20} /></span>
          <h1 className="font-head text-4xl font-bold text-[#3D405B]">Scripture Memory</h1>
        </div>

        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <Stat icon={Flame} label="Day streak" value={stats.streak} tone="#F4A261" />
            <Stat icon={CheckCircle2} label="Memorized" value={stats.memorized_count} tone="#81B29A" />
            <Stat icon={BookHeart} label="Learning" value={stats.learning_count} tone="#E07A5F" />
          </div>
        )}

        {verses.length > 0 && (
          <button data-testid="flashcards-btn" onClick={() => setFlash(true)}
            className="mb-8 flex items-center gap-2 rounded-full text-white px-6 py-3 font-semibold transition-transform hover:scale-105" style={{ background: accent }}>
            <Layers size={18} /> Quiz me with flashcards
          </button>
        )}

        <div className="rounded-2xl bg-white border border-[#EAE6DF] p-6 mb-8">
          <p className="font-head font-semibold text-[#3D405B] mb-3">{isParent ? "Assign a verse" : "Add a verse to memorize"}</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {VERSES.slice(0, 8).map((v) => (
              <button key={v.ref} onClick={() => preset(v)} className="text-xs px-3 py-1.5 rounded-full bg-[#F2EFE9] border border-[#EAE6DF] hover:border-[#81B29A] transition-colors">{v.ref}</button>
            ))}
          </div>
          <input data-testid="verse-ref" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Reference (e.g. Philippians 4:13)"
            className="w-full rounded-xl border border-[#EAE6DF] px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#81B29A] mb-3" />
          <textarea data-testid="verse-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Verse text…"
            className="w-full min-h-[80px] rounded-xl border border-[#EAE6DF] px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#81B29A]" />
          <button data-testid="add-verse-btn" onClick={add} className="mt-3 flex items-center gap-2 rounded-full text-white px-6 py-2.5 font-semibold transition-transform hover:scale-105" style={{ background: accent }}>
            <Plus size={16} /> {isParent ? "Assign verse" : "Add verse"}
          </button>
        </div>

        <div className="space-y-3">
          {verses.length === 0 && <p className="text-[#6B7280]">No verses yet — {isParent ? "assign one above." : "add one to start memorizing!"}</p>}
          {verses.map((v, i) => (
            <motion.div key={v.id} data-testid={`verse-${v.id}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className={`rounded-2xl border p-5 ${v.status === "memorized" ? "bg-[#81B29A]/10 border-[#81B29A]/40" : "bg-white border-[#EAE6DF]"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <Cross size={16} className="mt-1 shrink-0" style={{ color: accent }} />
                  <div>
                    <p className="stat text-sm font-semibold text-[#3D405B]">{v.reference}</p>
                    <p className="text-[#3D405B] leading-relaxed mt-0.5">"{v.text}"</p>
                    {v.status === "memorized" && <p className="text-xs text-[#81B29A] mt-1 flex items-center gap-1"><CheckCircle2 size={12} /> Memorized</p>}
                    <div className="mt-2"><ReadAloudButton testid={`verse-read-${v.id}`} label="Read aloud" text={`${v.reference}. ${v.text}`} tone={accent} /></div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!isParent && v.status !== "memorized" && (
                    <button data-testid={`memorize-${v.id}`} onClick={() => memorize(v.id)} className="text-xs rounded-full bg-[#81B29A] text-white px-3 py-1.5 hover:bg-[#6f9d87] transition-colors whitespace-nowrap">I know it!</button>
                  )}
                  <button onClick={() => remove(v.id)} className="text-[#6B7280] hover:text-[#E07A5F] transition-colors"><Trash2 size={15} /></button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </main>
      <AnimatePresence>
        {flash && <FlashcardModal verses={verses} accent={accent} onClose={() => setFlash(false)} />}
      </AnimatePresence>
    </div>
  );
}

function FlashcardModal({ verses, accent, onClose }) {
  const [i, setI] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const v = verses[i];
  const next = () => { setRevealed(false); setI((x) => (x + 1) % verses.length); };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 grid place-items-center p-6 bg-[#3D405B]/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div data-testid="flashcard-modal" initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9 }}
        onClick={(e) => e.stopPropagation()} className="w-full max-w-lg">
        <div className="flex items-center justify-between mb-3 text-white">
          <span className="stat text-sm">Card {i + 1} / {verses.length}</span>
          <button data-testid="flashcard-close" onClick={onClose} className="text-white/80 hover:text-white text-sm">Close</button>
        </div>
        <div data-testid="flashcard" onClick={() => setRevealed((r) => !r)}
          className="min-h-[280px] rounded-3xl bg-white p-8 flex flex-col items-center justify-center text-center cursor-pointer select-none grain relative overflow-hidden">
          <Cross size={20} style={{ color: accent }} />
          <p className="stat text-lg font-bold text-[#3D405B] mt-4">{v.reference}</p>
          {revealed ? (
            <p className="text-[#3D405B] leading-relaxed mt-4">"{v.text}"</p>
          ) : (
            <p className="text-[#6B7280] mt-4 flex items-center gap-2"><Eye size={16} /> Say it from memory, then tap to reveal</p>
          )}
        </div>
        <div className="flex items-center justify-center gap-3 mt-4">
          <ReadAloudButton testid="flashcard-read" label="Hear it" text={`${v.reference}. ${v.text}`} tone="#fff" />
          <button data-testid="flashcard-next" onClick={next}
            className="flex items-center gap-2 rounded-full text-white px-6 py-2.5 font-semibold transition-transform hover:scale-105" style={{ background: accent }}>
            Next card <ChevronRight size={16} />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-2xl bg-white border border-[#EAE6DF] p-5 text-center">
      <Icon size={22} className="mx-auto" style={{ color: tone }} />
      <p className="stat text-3xl font-bold text-[#3D405B] mt-2">{value}</p>
      <p className="text-xs text-[#6B7280]">{label}</p>
    </div>
  );
}
